import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Flex,
  Form,
  Modal,
  Spin,
  Tag,
  Typography
} from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  organizationsAccessListInfiniteOptions,
  type ActionEnum,
  UserArchiveJobStatusEnum,
  usersArchiveCreateMutation,
  usersArchiveDependenciesRetrieveOptions,
  usersArchiveJobsRetrieveOptions,
  usersArchiveReplaceAndArchiveCreateMutation
} from 'src/api/main'
import type {
  ArchiveEntityDependency,
  OrganizationsAccessListData,
  OrganizationUsers,
  PaginatedOrganizationUsersList
} from 'src/api/main'
import { InfiniteSelect } from 'src/components'
import { useShowApiErrors } from 'src/utils'

import { invalidateWorkspaceUserQueries } from '../hooks'
import type { WorkspaceUser } from '../types'
import { composeDisplayName } from '../utils'

const { Text } = Typography

type Props = {
  workspaceId: number
  open: boolean
  user: WorkspaceUser | null
  mode: ActionEnum
  onClose: () => void
}

type FormValues = { target: number }

const useEntityLabel = () => {
  const { i18n } = useTranslation()
  const lang = i18n.language

  return (e: ArchiveEntityDependency) => {
    const byLang = lang.startsWith('ro')
      ? e.label_ro
      : lang.startsWith('ru')
        ? e.label_ru
        : e.label_en

    return byLang || e.label_en || e.code
  }
}

export const UserArchiveModal = ({
  workspaceId,
  open,
  user,
  mode,
  onClose
}: Props) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const showApiErrors = useShowApiErrors()
  const [form] = Form.useForm<FormValues>()
  const entityLabel = useEntityLabel()

  const isArchive = mode === 'archive'
  const userId = user?.user.id ?? 0
  const [jobId, setJobId] = useState<number | null>(null)

  const finishSuccess = async (msg: string) => {
    message.success(msg)
    if (user) {
      await invalidateWorkspaceUserQueries(qc, {
        workspaceId,
        organizationAccessId: user.id,
        userId: user.user.id
      })
    }
    onClose()
  }

  const depsQuery = useQuery({
    ...usersArchiveDependenciesRetrieveOptions({
      path: { user_id: userId },
      query: { organization_id: workspaceId }
    }),
    enabled: open && isArchive && userId > 0
  })
  const deps = depsQuery.data
  const hasBlockers = deps?.has_blockers ?? false

  const archive = useMutation({
    ...usersArchiveCreateMutation(),
    onSuccess: () =>
      finishSuccess(
        t(
          isArchive
            ? 'users.modals.archive.success'
            : 'users.modals.unarchive.success'
        )
      ),
    onError: (e) => {
      showApiErrors(e)
      if (isArchive) void depsQuery.refetch()
    }
  })

  const replace = useMutation({
    ...usersArchiveReplaceAndArchiveCreateMutation(),
    onSuccess: (job) => setJobId(job.id),
    onError: (e) => showApiErrors(e, form)
  })

  const jobQuery = useQuery({
    ...usersArchiveJobsRetrieveOptions({ path: { job_id: jobId ?? 0 } }),
    enabled: jobId != null,
    refetchInterval: (query) => {
      const s = query.state.data?.status

      return s === UserArchiveJobStatusEnum.PENDING ||
        s === UserArchiveJobStatusEnum.PROCESSING
        ? 1500
        : false
    }
  })
  const jobStatus = jobQuery.data?.status

  useEffect(() => {
    if (jobStatus === UserArchiveJobStatusEnum.SUCCEEDED) {
      void finishSuccess(t('users.modals.archive.success'))
    } else if (jobStatus === UserArchiveJobStatusEnum.FAILED) {
      message.error(t('users.modals.archive.jobError'))
    }
  }, [jobStatus])

  const jobRunning =
    jobId != null &&
    (jobStatus == null ||
      jobStatus === UserArchiveJobStatusEnum.PENDING ||
      jobStatus === UserArchiveJobStatusEnum.PROCESSING)

  const submitReplace = (values: FormValues) => {
    if (!user) return
    replace.mutate({
      path: { user_id: user.user.id },
      body: { organization_id: workspaceId, target_user_id: values.target }
    })
  }

  const onOk = () => {
    if (!user) return
    if (!isArchive) {
      archive.mutate({
        path: { user_id: user.user.id },
        body: { organization_id: workspaceId, action: 'unarchive' }
      })
      return
    }
    if (hasBlockers) {
      form.submit()
      return
    }
    archive.mutate({
      path: { user_id: user.user.id },
      body: { organization_id: workspaceId, action: 'archive' }
    })
  }

  const name = user
    ? composeDisplayName(
        user.user.first_name,
        user.user.last_name,
        user.user.email
      )
    : ''

  const busy = archive.isPending || replace.isPending || jobRunning
  const depsLoading = depsQuery.isLoading
  const depsFailed = depsQuery.isError

  const okText = !isArchive
    ? t('users.modals.unarchive.submit')
    : hasBlockers
      ? t('users.modals.archive.submitReplace')
      : t('users.modals.archive.submit')

  // Entries with something active worth showing.
  const services = Object.values(deps?.services ?? {}).filter((svc) =>
    svc.entities.some((e) => e.active_count > 0)
  )

  return (
    <Modal
      open={open}
      centered
      title={t(
        isArchive
          ? 'users.modals.archive.title'
          : 'users.modals.unarchive.title'
      )}
      okText={okText}
      cancelText={t('users.modals.cancel')}
      okType={isArchive ? 'danger' : 'primary'}
      okButtonProps={{
        loading: busy,
        disabled: isArchive && (depsLoading || depsFailed)
      }}
      cancelButtonProps={{ disabled: busy }}
      onOk={onOk}
      onCancel={() => {
        if (!busy) onClose()
      }}
      afterOpenChange={(o) => {
        if (!o) {
          setJobId(null)
          form.resetFields()
        }
      }}
      destroyOnHidden
    >
      <p style={{ marginTop: 0, color: 'var(--ant-color-text-secondary)' }}>
        {t(
          isArchive
            ? 'users.modals.archive.description'
            : 'users.modals.unarchive.description',
          { name }
        )}
      </p>

      {isArchive && depsLoading ? (
        <Flex align="center" gap={8}>
          <Spin size="small" />
          <Text type="secondary" style={{ fontSize: 13 }}>
            {t('users.modals.archive.checking')}
          </Text>
        </Flex>
      ) : null}

      {isArchive && depsFailed ? (
        <Alert
          type="error"
          showIcon
          title={t('users.modals.archive.checkFailed')}
        />
      ) : null}

      {isArchive && deps && !depsLoading ? (
        <>
          {services.length > 0 ? (
            <div style={{ marginBottom: 12 }}>
              <Text
                type="secondary"
                className="ui-eyebrow"
                style={{ display: 'block', marginBottom: 6 }}
              >
                {t('users.modals.archive.depsTitle')}
              </Text>
              <Flex vertical gap={8}>
                {services.map((svc) => (
                  <Flex key={svc.service} align="baseline" gap={8} wrap="wrap">
                    <Text strong style={{ fontSize: 12 }}>
                      {t(`users.modals.archive.services.${svc.service}`, {
                        defaultValue: svc.service.toUpperCase()
                      })}
                    </Text>
                    {svc.entities
                      .filter((e) => e.active_count > 0)
                      .map((e) => (
                        <Tag
                          key={e.code}
                          color={e.active_count > 0 ? 'warning' : undefined}
                          style={{ marginInlineEnd: 0, fontSize: 11 }}
                        >
                          {entityLabel(e)} ·{' '}
                          {t('users.modals.archive.entityCount', {
                            count: e.active_count
                          })}
                        </Tag>
                      ))}
                  </Flex>
                ))}
              </Flex>
            </div>
          ) : null}

          <Alert
            type={hasBlockers ? 'warning' : 'info'}
            showIcon
            title={t(
              hasBlockers
                ? 'users.modals.archive.hasBlockers'
                : 'users.modals.archive.noBlockers'
            )}
            style={{ marginBottom: hasBlockers ? 12 : 0 }}
          />

          {hasBlockers ? (
            <Form<FormValues>
              form={form}
              layout="vertical"
              onFinish={submitReplace}
              preserve={false}
            >
              <Form.Item
                name="target"
                label={t('users.modals.archive.replaceLabel')}
                rules={[
                  {
                    required: true,
                    message: t('users.modals.archive.replaceRequired')
                  }
                ]}
              >
                <InfiniteSelect<
                  OrganizationsAccessListData,
                  PaginatedOrganizationUsersList,
                  OrganizationUsers
                >
                  disabled={jobRunning}
                  placeholder={t('users.modals.archive.replacePlaceholder')}
                  getQueryFunction={organizationsAccessListInfiniteOptions}
                  baseOptions={{ query: { organization: workspaceId } }}
                  getOptions={({ options }) =>
                    options
                      // Can't hand records to the very user being archived.
                      .filter((u) => u.user.id !== user?.user.id)
                      .map((u) => ({
                        value: u.user.id,
                        label: composeDisplayName(
                          u.user.first_name,
                          u.user.last_name,
                          u.user.email
                        )
                      }))
                  }
                />
              </Form.Item>
            </Form>
          ) : null}

          {hasBlockers ? (
            <Text
              type="secondary"
              style={{ display: 'block', fontSize: 12, marginTop: 4 }}
            >
              {t('users.modals.archive.manualHint')}
            </Text>
          ) : null}

          {jobId != null ? (
            <Flex align="center" gap={8} style={{ marginTop: 4 }}>
              {jobRunning ? <Spin size="small" /> : null}
              <Text type="secondary" style={{ fontSize: 13 }}>
                {t(
                  `users.modals.archive.job.${jobStatus ?? UserArchiveJobStatusEnum.PENDING}`
                )}
              </Text>
            </Flex>
          ) : null}
        </>
      ) : null}
    </Modal>
  )
}
